import { Component, ChangeDetectionStrategy, computed, inject, input, LOCALE_ID, output } from '@angular/core';
import { Loading } from '../../../../utils/loading/loading';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { Invoices } from '../../../../pages/admin-dashboard/admin-invoice/admin-invoice.model';
import { toInvoiceTableRows } from './Invoice-table.mapper';

export type LoadState = 'loading' | 'success' | 'error';

const ACTIONS = [{ id: 1, titleEn: 'View Invoice Details', titleAr: 'عرض تفاصيل الفاتورة' }];

@Component({
  selector: 'app-invoice-table',
  standalone: true,
  imports: [Loading, ErrorData, Tables, Pagination],
  templateUrl: './invoice-table.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvoiceTable {
  private locale = inject(LOCALE_ID);

  invoices = input<Invoices[] | null>(null);
  state = input<LoadState>('loading');
  errorMessage = input<string | null>(null);
  currentPage = input<number>(1);
  itemsPerPage = input<number>(25);
  totalItems = input<number>(0);
  totalPages = input<number>(1);
  isFiltersApplied = input<boolean>(false);

  pageChange = output<number>();
  pageSizeChange = output<number>();
  rowClick = output<number>();
  getInvoices = output<void>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  rows = computed(() => toInvoiceTableRows(this.invoices() ?? []));

  headerItems = [
    { key: 'invoiceId', label: $localize`:@@invoiceListTableInvoiceId:Invoice ID`, type: 'link' },
    { key: 'orderId', label: $localize`:@@invoiceListTableOrderId:Order ID`, type: 'link' },
    { key: 'pharmacyOwner', label: $localize`:@@invoiceListTablePharmacy:Pharmacy / Owner` },
    { key: 'status', label: $localize`:@@invoiceListTableStatus:Status`, type: 'status' },
    {
      key: 'totalPharmacyPrice',
      label: $localize`:@@invoiceListTableTotalPharmacyPrice:Total Pharmacy Price`,
      type: 'currency',
    },
    { key: 'createdOnBy', label: $localize`:@@invoiceListTableCreatedOn:Created On / By` },
    { key: 'actions', label: '', type: 'actions' },
  ];

  actions = ACTIONS;

  startItem = computed(() => {
    if (this.totalItems() === 0) return 0;
    return (this.currentPage() - 1) * this.itemsPerPage() + 1;
  });

  endItem = computed(() => {
    const end = this.currentPage() * this.itemsPerPage();
    return end > this.totalItems() ? this.totalItems() : end;
  });

  pageNumbers = computed(() => {
    const total = this.totalPages();
    const current = this.currentPage();
    const delta = 2;
    const range: number[] = [1];

    if (total <= 1) return range;

    for (let i = current - delta; i <= current + delta; i++) {
      if (i > 1 && i < total) {
        range.push(i);
      }
    }

    range.push(total);

    const result: number[] = [];
    let prev: number | undefined;

    for (const i of range) {
      if (prev !== undefined) {
        if (i - prev === 2) {
          result.push(prev + 1);
        } else if (i - prev !== 1) {
          result.push(-1);
        }
      }

      result.push(i);
      prev = i;
    }

    return result;
  });

  handleAction(e: { event: Event; actionId: number; entity: any }) {
    const { event, actionId, entity } = e;

    event?.stopPropagation();

    if (actionId === 1) {
      this.rowClick.emit(entity.id);
    }
  }

  onPreviousPage() {
    this.pageChange.emit(this.currentPage() - 1);
  }

  onNextPage() {
    this.pageChange.emit(this.currentPage() + 1);
  }

  onGoToPage(page: number) {
    this.pageChange.emit(page);
  }

  onChangePageNumber(size: number) {
    this.pageSizeChange.emit(Number(size));
  }

  onPageSizeChange() {}
}
